import api from './axiosConfig';

export type ScreenerSearchRequest = {
  market?: string;
  keyword?: string | null;
  minMarketCap?: number | null;
  minTradingValue?: number | null;
  minScore?: number | null;
  styleTags?: string[];

  minRoicWaccSpread?: number | null;
  minRevenueCagr3y?: number | null;
  minOperatingMargin?: number | null;
  minRoe?: number | null;

  foreignNetBuy20dPositive?: boolean | null;
  institutionNetBuy20dPositive?: boolean | null;
  minSectorFlowScore?: number | null;
  minMacroAlignmentScore?: number | null;
  minDisclosureImpactScore?: number | null;
  excludeRiskAvoid?: boolean | null;

  sectorCode?: string | null;
  minEtfSectorChangePct?: number | null;
  maxEtfSectorChangePct?: number | null;

  minPer?: number | null;
  maxPer?: number | null;
  minPbr?: number | null;
  maxPbr?: number | null;
  minPegRatio?: number | null;
  maxPegRatio?: number | null;
  maxPriceToSales?: number | null;
  maxEvToOperatingIncome?: number | null;

  minDividendYield?: number | null;
  minFcfYield?: number | null;
  maxDebtToEquity?: number | null;
  maxNetDebtToOperatingIncome?: number | null;

  minPriceChangePct?: number | null;
  maxPriceChangePct?: number | null;
  minTradingValueRatio20d?: number | null;
  minHistoricalVolatility20d?: number | null;
  maxHistoricalVolatility20d?: number | null;
  minAtr14dPct?: number | null;
  maxAtr14dPct?: number | null;
  maxStockBeta60d?: number | null;
  maxMacroShockScore?: number | null;
  foreignNetBuy5dPositive?: boolean | null;
  institutionNetBuy5dPositive?: boolean | null;
  minPricePosition52w?: number | null;
  maxPricePosition52w?: number | null;

  sortBy?: string | null;
  sortDirection?: 'ASC' | 'DESC' | string | null;
  page?: number;
  size?: number;
};

export type ScreenerSearchResponse = {
  totalCount?: number;
  page?: number;
  size?: number;
  hasNext?: boolean;
  items?: Array<Record<string, any>>;
};

export type ScreenerCondition = {
  key?: string;
  label?: string;
  passed?: boolean | null;
  message?: string;
  value?: any;
  threshold?: any;
};

export type ScreenerDiagnosisResponse = {
  found?: boolean;
  ticker?: string;
  corpName?: string;
  market?: string;
  tradeDate?: string;
  closePrice?: number | null;
  priceChangePct?: number | null;
  tradingValue?: number | null;
  marketCap?: number | null;
  score?: number | null;

  decision?: string;
  decisionLabel?: string;
  decisionTone?: 'positive' | 'negative' | 'warning' | 'neutral' | string;

  buySignal?: boolean | null;
  sellRisk?: boolean | null;
  holdSignal?: boolean | null;
  summary?: string;

  buyScore?: number | null;
  holdScore?: number | null;
  sellRiskScore?: number | null;
  macroRiskScore?: number | null;

  buyDecisionLabel?: string;
  holdDecisionLabel?: string;
  sellRiskLabel?: string;
  macroRiskLabel?: string;
  macroReasons?: string[];

  buyConditions?: ScreenerCondition[];
  sellConditions?: ScreenerCondition[];
  reasons?: string[];
  metrics?: Record<string, any>;
  macroMetrics?: Record<string, any>;
  raw?: Record<string, any>;

  message?: string;
};

export type ProfessionalScreenerFilterOptions = {
  sectorOptions?: Array<{
    sectorCode?: string;
    sector_code?: string;
    sectorName?: string;
    sector_name?: string;
    representativeEtfTicker?: string;
    representative_etf_ticker?: string;
    stockCount?: number;
    stock_count?: number;
  }>;
  styleOptions?: Array<{ value: string; label: string }>;
  sortOptions?: string[];
};


export type ProfessionalFinancialHistoryItem = {
  ticker?: string;
  corpCode?: string;
  corpName?: string;
  year?: string;
  reprtCode?: string;
  fsDiv?: string;
  revenue?: number | null;
  operatingIncome?: number | null;
  netIncome?: number | null;
  totalAssets?: number | null;
  totalLiabilities?: number | null;
  totalEquity?: number | null;
  operatingCashFlow?: number | null;
  capex?: number | null;
  cashAndEquivalents?: number | null;
  shortTermBorrowings?: number | null;
  longTermBorrowings?: number | null;
  interestExpense?: number | null;
  totalBorrowings?: number | null;
  netDebt?: number | null;
  freeCashFlow?: number | null;
  ocfMarginPct?: number | null;
  capexToRevenuePct?: number | null;
  fcfMarginPct?: number | null;
  ocfToNetIncome?: number | null;
  interestCoverageAnnual?: number | null;
  netDebtToOperatingIncomeAnnual?: number | null;
  updatedAt?: string | null;
};

export type ProfessionalFinancialHistoryResponse = {
  ticker?: string;
  found?: boolean;
  periodType?: 'ANNUAL' | string;
  reportCode?: string;
  source?: string;
  basis?: string;
  latestYear?: string | null;
  count?: number;
  items?: ProfessionalFinancialHistoryItem[];
  message?: string;
};

export type ProfessionalScreenerTickerResolveResult = {
  found: boolean;
  ticker?: string;
  corpName?: string;
  market?: string;
  message?: string;
  raw?: Record<string, any>;
};

function normalizeTickerCandidate(value: any): string {
  const raw = String(value ?? '').trim().replace(/^A/i, '');
  const matched = raw.match(/\b(\d{6})\b/)?.[1] ?? raw.match(/^(\d{6})$/)?.[1];
  return matched || '';
}

function pickFirstArray(data: any): Array<Record<string, any>> {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.list)) return data.list;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.result)) return data.result;
  return [];
}

function getTicker(row: Record<string, any>): string {
  return normalizeTickerCandidate(
      row?.ticker ?? row?.stockCode ?? row?.stock_code ?? row?.code ?? row?.symbol,
  );
}

function getCorpName(row: Record<string, any>): string {
  return String(
      row?.corpName ?? row?.corp_name ?? row?.stockName ?? row?.stock_name ?? row?.name ?? '',
  ).trim();
}

export async function searchProfessionalScreener(request: ScreenerSearchRequest,): Promise<ScreenerSearchResponse> {
  const res = await api.post('/screener/professional/search', request);
  return res.data;
}

export async function getProfessionalScreenerFilterOptions(): Promise<ProfessionalScreenerFilterOptions> {
  const res = await api.get('/screener/professional/filter-options');
  return res.data;
}

export async function getProfessionalFinancialHistory(ticker: string, years = 5,): Promise<ProfessionalFinancialHistoryResponse> {
  const normalizedTicker = normalizeTickerCandidate(ticker) || String(ticker ?? '').trim();
  const res = await api.get(
      `/screener/professional/financial-history/${encodeURIComponent(normalizedTicker)}`,
      { params: { years: Math.max(1, Math.min(10, Number(years) || 5)) } },
  );
  const data = res.data || {};
  return {
    ...data,
    items: Array.isArray(data?.items) ? data.items : [],
  };
}


function nullToUndefined<T extends Record<string, any>>(obj: T): T {
  const out: Record<string, any> = {...(obj || {})};
  Object.keys(out).forEach((key) => {
    if (out[key] === null) out[key] = undefined;
  });
  return out as T;
}

function normalizeDiagnosisResponse(data: any): ScreenerDiagnosisResponse {
  const next = nullToUndefined<ScreenerDiagnosisResponse>(data || {});
  next.ticker = normalizeTickerCandidate(next.ticker) || undefined;
  next.decisionTone = next.decisionTone || undefined;
  next.buyConditions = Array.isArray(next.buyConditions)
      ? next.buyConditions.map((row: any) => nullToUndefined<ScreenerCondition>(row || {}))
      : [];
  next.sellConditions = Array.isArray(next.sellConditions)
      ? next.sellConditions.map((row: any) => nullToUndefined<ScreenerCondition>(row || {}))
      : [];
  next.macroReasons = Array.isArray(next.macroReasons) ? next.macroReasons.filter(Boolean).map(String) : [];
  next.reasons = Array.isArray(next.reasons) ? next.reasons.filter(Boolean).map(String) : [];
  return next;
}

export async function diagnoseProfessionalScreener(ticker: string,): Promise<ScreenerDiagnosisResponse> {
  const normalizedTicker = normalizeTickerCandidate(ticker) || String(ticker ?? '').trim();
  const res = await api.get(`/screener/professional/diagnosis/${encodeURIComponent(normalizedTicker)}`);
  return normalizeDiagnosisResponse(res.data);
}

export async function resolveProfessionalScreenerTicker(keywordOrTicker: string,): Promise<ProfessionalScreenerTickerResolveResult> {
  const keyword = String(keywordOrTicker ?? '').trim();
  if (!keyword) {
    return {found: false, message: '종목명 또는 6자리 종목코드를 입력해 주세요.'};
  }

  const directTicker = normalizeTickerCandidate(keyword);
  if (directTicker) {
    return {found: true, ticker: directTicker};
  }

  const data = await searchProfessionalScreener({
    keyword,
    market: 'ALL',
    minTradingValue: null,
    minScore: null,
    excludeRiskAvoid: false,
    sortBy: 'score',
    sortDirection: 'DESC',
    page: 0,
    size: 10,
  });

  const rows = pickFirstArray(data);
  const first = rows.find((row) => getTicker(row));
  if (!first) {
    return {
      found: false,
      message: '검색 결과에서 종목을 찾지 못했습니다.',
    };
  }

  return {
    found: true,
    ticker: getTicker(first),
    corpName: getCorpName(first),
    market: String(first?.market ?? first?.marketType ?? first?.exchange ?? '').trim(),
    raw: first,
  };
}
